import React, { useState, useEffect } from 'react';
import { Plus, Tag } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { expensesAPI } from '../../services/api';

export default function ExpenseCategories() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category_name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchList = () => {
    setLoading(true);
    expensesAPI.getCategories().then((res) => setList(res.data || [])).catch(() => setList([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category_name.trim()) return;
    setSaving(true);
    try {
      await expensesAPI.createCategory(form);
      setShowForm(false);
      setForm({ category_name: '', description: '' });
      fetchList();
    } catch (err) {
      alert(err.message || 'Failed to add category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Tag className="h-7 w-7 text-ga-primary" />
              Expense Categories
            </h1>
            <p className="text-ga-text-muted mt-1">Manage expense categories</p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="ga-btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Category
          </button>
        </div>

        {showForm && (
          <div className="ga-card rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">New Category</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Name *</label>
                <input
                  type="text"
                  value={form.category_name}
                  onChange={(e) => setForm((f) => ({ ...f, category_name: e.target.value }))}
                  className="ga-input w-full rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="ga-input w-full rounded-lg"
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={saving} className="ga-btn-primary px-4 py-2 rounded-lg">
                  {saving ? 'Saving...' : 'Add'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setForm({ category_name: '', description: '' }); }} className="ga-btn-ghost px-4 py-2 rounded-lg">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="ga-card rounded-xl shadow-sm border overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-ga-text-muted">Loading...</div>
          ) : (
            <ul className="divide-y divide-ga-border-subtle">
              {list.map((c) => (
                <li key={c.id} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-ga-text-primary">{c.category_name}</p>
                    {c.description && <p className="text-sm text-ga-text-muted">{c.description}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {list.length === 0 && !loading && <div className="py-12 text-center text-ga-text-muted">No categories. Add one to get started.</div>}
        </div>
      </div>
    </DashboardLayout>
  );
}
